import { FC } from 'react';

import { useTranslation } from 'react-i18next';
import SVGIcon from 'react-inlinesvg';

import IconCheckmark from '@assets/svg/icon-checkmark.svg';
import { AppLink, TableCapabilities } from '@components';
import { Source } from '@store';
import { withLinks } from '@utilities';

type Props = {
	data: Source;
	className: string;
};

const SourceDetails: FC<Readonly<Props>> = ({ data, className }) => {
	const { t } = useTranslation();

	return (
		<div className={className} data-testid="table-details">
			<aside>
				{data.homepage && (
					<p>
						<AppLink type="external" href={data.homepage}>
							{data.homepage}
						</AppLink>
					</p>
				)}

				{data.forwarder && (
					<>
						<p>
							<SVGIcon src={IconCheckmark} />

							{t('This source is known to forward email')}
						</p>

						{data.notes_fwd && (
							<p>
								<em>Forwarding notes:</em>

								<small // nosemgrep: eslint.react-dangerouslysetinnerhtml
									dangerouslySetInnerHTML={{ __html: withLinks(data.notes_fwd) }}
								/>
							</p>
						)}
					</>
				)}
			</aside>

			{data.forwarder_only ? (
				<table>
					<thead>
						<tr>
							<th colSpan={2}>{t('Forwarding Characteristics')}</th>
						</tr>
					</thead>

					<tbody>
						<TableCapabilities
							title={t('Forwarding preserves DKIM signatures')}
							condition={data.fwd_preserves_dkim}
						/>

						<TableCapabilities
							title={t('Uses own domain in envelope (SPF)')}
							condition={data.fwd_uses_own_envelope}
						/>

						{data.notes_fwd && (
							<tr>
								<td colSpan={2}>
									<p>Notes:</p>

									<small // nosemgrep: eslint.react-dangerouslysetinnerhtml
										dangerouslySetInnerHTML={{ __html: withLinks(data.notes_fwd) }}
									/>
								</td>
							</tr>
						)}
					</tbody>
				</table>
			) : (
				<table>
					<thead>
						<tr>
							<th colSpan={2}>{t('DMARC Capabilities')}</th>
						</tr>
					</thead>

					<tbody>
						<TableCapabilities
							title={t('Capable of generating DMARC compliant email for others')}
							subtitle={withLinks(data.notes_dmarc)}
							condition={data.capable_dmarc}
						/>

						<TableCapabilities
							title={t('Capable of signing email with DKIM for others')}
							subtitle={withLinks(data.notes_dkim)}
							condition={data.capable_dkim}
						/>

						<TableCapabilities
							title={t('Capable of sending SPF compliant email for others')}
							subtitle={withLinks(data.notes_spf)}
							condition={data.capable_spf}
						/>
					</tbody>
				</table>
			)}
		</div>
	);
};

export default SourceDetails;
